<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Lab Instrument Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading class="mb-3">
      <span class="float-left mr-2">{{ instrument_instance.name }}</span>
      <b-badge
        v-for="(badge, index) in context_badges"
        :key="index"
        pill
        variant="warning"
        class="float-left font-2xl mt-3 ml-1"
        >{{ badge }}</b-badge
      >
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete instrument instance')"
              title="Delete instrument"
              class="btn btn-danger ml-1"
              @click="showDeleteModal"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read instrument instance'">
      <template #gated-content>
        <instrument-instance-card :instrument_instance="instrument_instance" />
        <instrument-instance-detail-cardstack
          :instrument_id="instrument_instance.instrument_id"
        />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import InstrumentInstanceCard from "@/components/lab/instrumentinstance/InstrumentInstanceCard";
import InstrumentInstanceDetailCardstack from "@/components/lab/instrumentinstance/InstrumentInstanceDetailCardstack";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { instrumentInstanceNotifications } from "@/shared/notifications";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";

Vue.use(VueNotifications, notificationOptions);

const InstrumentInstanceRepository =
  LabRepositoryFactory.get("instrumentInstance");

export default {
  name: "InstrumentInstanceDetail",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    InstrumentInstanceCard,
    InstrumentInstanceDetailCardstack,
    PermissionAlertGate,
  },
  mixins: [ToastNotifications],
  data() {
    return {
      loading: true,
      instrument_instance_id: this.$route.params.instrument_instance_id || null,
      instrument_instance: null,
      context_badges: [],
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-search",
            linkto: `/instrument-instances/${this.instrument_instance_id}/audit-trail`,
            title: "Back to Audit Trail",
            variant: "dark",
          },
          {
            icon: "fa-list",
            linkto: "/instrument-instances",
            title: "InstrumentInstance list",
          },
          {
            icon: "fa-pencil",
            linkto: `/instrument-instances/${this.instrument_instance_id}/edit`,
            title: "Edit Lab Instrument",
            variant: "warning",
            permission: this.checkPermission("edit instrument instance"),
          },
        ],
      };
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this instrument instance. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await InstrumentInstanceRepository.suspend(
                this.instrument_instance_id
              );
  
              this.deleteSuccess();
              this.$router.push("/instrument-instances");
            } catch ({ response }) {
              const { status, data } = response.response;
  
              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.instrument_instance = await getModel({
      model_type: "instrument_instance",
      id: this.instrument_instance_id,
      message: this.$bvToast,
    });

    if (this.instrument_instance) {
      const { in_storage, out_of_service } = this.instrument_instance;

      in_storage && this.context_badges.push("In Storage");
      out_of_service && this.context_badges.push("Out Of Service");

      this.loading = false;
    } else {
      this.$router.back();
    }
  },
  notifications: instrumentInstanceNotifications,
};
</script>
